import '@ant-design/v5-patch-for-react-19';
import { initCWElements } from '@cw-elements/config';
import { initMfes } from '@cw-elements/mfe-setup/init';
import { initialize as initLDClient } from 'launchdarkly-js-client-sdk';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { ENV_VARIABLES } from '@/constants';
import {
  CONSENT_ENABLED_FLAG_KEY,
  DEFAULT_CONSENT_ENABLED,
  DEFAULT_SESSION_REPLAY_SAMPLE_RATE,
  DEFAULT_SESSION_SAMPLE_RATE,
  getAccessToken,
  installEarlyErrorCapture,
  reportBootstrapError,
  startAnalytics
} from '@/libs';

import App from './App';

import './main.css';

const LD_INIT_TIMEOUT_SECONDS = 5;

const BOOTSTRAP_FAILURE_MESSAGE =
  'We could not start the application. Please refresh the page; if the problem persists, contact support.';

// Renders a dependency-free message when bootstrap fails before React can mount
// — at this point styled-components/antd may be unavailable, so write raw DOM.
const renderBootstrapFallback = () => {
  const root = document.getElementById('root');

  if (root) {
    root.textContent = BOOTSTRAP_FAILURE_MESSAGE;
  }
};

/** Id of the `index.html` unsupported-browser notice. Duplicated there by
 * necessity — that markup must not depend on this bundle to render. */
const UNSUPPORTED_BROWSER_NOTICE_ID = 'unsupported-browser';

// Tells the `index.html` boot watchdog the bundle parsed and started running,
// so it stops short of showing the unsupported-browser notice. Must stay the
// first statement in this module, and nothing that can throw may sit between
// it and the `try` below: once the flag is set the watchdog can no longer
// report anything, so from here on every failure is this module's to report.
window.__PANORAMA_BOOTED__ = true;

// Retract the notice as well as suppress it. The watchdog fires on a timer, so
// a merely slow load — the shim CDN in `index.html` is render-blocking — can
// show it on a perfectly supported browser before the bundle arrives. Nothing
// else ever hides it again, so without this the app renders under a permanent
// "not supported" alert. Optional-chained on purpose: this sits outside the
// `try` and must not be able to throw.
document
  .getElementById(UNSUPPORTED_BROWSER_NOTICE_ID)
  ?.style.setProperty('display', 'none');

try {
  // Buffer errors thrown before Datadog RUM installs its own global handlers,
  // so pre-init bootstrap failures are not lost. Must run before any other
  // init. Inside the `try` because `reportBootstrapError` does not depend on
  // it — a throw here still reports and still renders the fallback.
  installEarlyErrorCapture();

  initCWElements({
    environment: ENV_VARIABLES.ENV_CW_COMPONENTS,
    hostAppId: ENV_VARIABLES.CLIENT_ID,
    token: getAccessToken()
  });

  void initMfes({
    'topbar-navigation': `${ENV_VARIABLES.CW_ELEMENTS_MFES}/topbar-navigation`,
    'farm-mgt': `${ENV_VARIABLES.CW_ELEMENTS_MFES}/farm-mgt`,
    hostAppId: `${ENV_VARIABLES.PANORAMA_APP_ID}`
  });

  // Bootstrap LD before analytics so the RUM/Logs sample rates and the
  // consent gate can be flag-driven. The same LDClient instance is reused by
  // <LDProvider> below — no double init.
  const ldClient = initLDClient(ENV_VARIABLES.LD_CLIENT_SIDE_ID, {
    kind: 'user',
    anonymous: true
  });

  try {
    await ldClient.waitForInitialization(LD_INIT_TIMEOUT_SECONDS);
  } catch (error) {
    console.warn(
      'LaunchDarkly bootstrap failed; falling back to the default analytics consent gate and Datadog sample rates.',
      error
    );
  }

  // Deliberately not awaited: analytics is not a rendering dependency, and the
  // provider is third-party code whose promise can hang. `startAnalytics` owns
  // the failure reporting and the early-error flush.
  startAnalytics({
    isConsentEnabled: ldClient.variation(
      CONSENT_ENABLED_FLAG_KEY,
      DEFAULT_CONSENT_ENABLED
    ),
    sessionSampleRate: ldClient.variation(
      'configure-web-datadog-rum-session-sample-rate',
      DEFAULT_SESSION_SAMPLE_RATE
    ),
    sessionReplaySampleRate: ldClient.variation(
      'configure-web-datadog-rum-session-replay-sample-rate',
      DEFAULT_SESSION_REPLAY_SAMPLE_RATE
    )
  });

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <LDProvider
        clientSideID={ENV_VARIABLES.LD_CLIENT_SIDE_ID}
        ldClient={ldClient}>
        <App />
      </LDProvider>
    </StrictMode>
  );
} catch (error) {
  reportBootstrapError(error);
  renderBootstrapFallback();
}
